import styles from './UploadUsers.module.css';
import Button from '../../Button/Button';
import { uploadUsersUrl, PREFIX } from '../../../helpers/constants';
import React, { useRef, useState } from 'react';
import useApi from '../../../hooks/useApi.hook';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import Spinner from '../../Spinner/Spinner';

export function UploadUsers({ setModalOpen, setRefreshKey }) {
	const api = useApi();
	const fileInputRef = useRef(null);

	const [file, setFile] = useState(null);
	const [createProf, setCreateProf] = useState(false);
	const [createDiv, setCreateDiv] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(undefined);

	const [phase, setPhase] = useState('upload'); // 'upload' | 'confirm'
	const [draft, setDraft] = useState(null);

	const [confirmLoading, setConfirmLoading] = useState(false);
	const [confirmResult, setConfirmResult] = useState(null); // null | 'confirmed' | 'partial' | 'rejected'
	const [confirmSummary, setConfirmSummary] = useState(null); // { created, failed, errors }

	const downloadBlob = (blob, filename) => {
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	const handleUpload = async () => {
		if (!file) return;
		setLoading(true);
		setError(undefined);
		try {
			const formData = new FormData();
			formData.append('file', file);

			const { data } = await api.post(
				`${uploadUsersUrl}?create_prof=${createProf}&create_div=${createDiv}`,
				formData,
				{ headers: { 'Content-Type': 'multipart/form-data' } }
			);

			const previewResp = await api.get(
				`${uploadUsersUrl}/${data.draft_id}/preview`,
				{ responseType: 'blob' }
			);
			downloadBlob(previewResp.data, `upload_result_${data.draft_id}.xlsx`);

			setDraft(data);
			setPhase('confirm');
		} catch (e) {
			setError(e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка');
		} finally {
			setLoading(false);
		}
	};

	const showResultAndClose = (result) => {
		setConfirmResult(result);
		setTimeout(() => {
			setModalOpen(false);
			setRefreshKey((prev) => prev + 1);
		}, 1500);
	};

	const handleConfirm = async () => {
		setConfirmLoading(true);
		setError(undefined);
		try {
			const { data } = await api.post(`${uploadUsersUrl}/${draft.draft_id}/confirm`);
			if (data.failed_users > 0) {
				setConfirmSummary({ created: data.created_users, failed: data.failed_users, errors: data.errors });
				setConfirmResult('partial');
				setRefreshKey((prev) => prev + 1);
			} else {
				showResultAndClose('confirmed');
			}
		} catch (e) {
			setError(e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка');
		} finally {
			setConfirmLoading(false);
		}
	};

	const handleReject = async () => {
		setConfirmLoading(true);
		setError(undefined);
		try {
			await api.delete(`${uploadUsersUrl}/${draft.draft_id}`);
			showResultAndClose('rejected');
		} catch (e) {
			setError(e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка');
		} finally {
			setConfirmLoading(false);
		}
	};

	const renderUploadBtnText = () => {
		if (loading) return <Spinner />;
		return 'Загрузить';
	};

	return (
		<div className={styles.wrap}>
			<h1 className={styles.title}>Загрузка сотрудников из файла</h1>
			<ErrorMessage error={error} onClose={() => setError(undefined)} />

			{phase === 'upload' && (
				<div className={styles.content}>
					<input
						ref={fileInputRef}
						type="file"
						accept=".xlsx,.xls"
						className={styles.hiddenInput}
						onChange={(e) => setFile(e.target.files[0] || null)}
					/>
					<button
						className={styles.fileBtn}
						onClick={() => fileInputRef.current.click()}
					>
						{file ? file.name : 'Выбрать файл…'}
					</button>
					<a
						href={`${PREFIX}/api/static/templates/upload_template.xlsx`}
						download
						className={styles.templateLink}
					>
						Скачать шаблон
					</a>

					<label className={styles.checkbox}>
						<input
							type="checkbox"
							checked={createProf}
							onChange={(e) => setCreateProf(e.target.checked)}
						/>
						Создавать новые профессии
					</label>
					<label className={styles.checkbox}>
						<input
							type="checkbox"
							checked={createDiv}
							onChange={(e) => setCreateDiv(e.target.checked)}
						/>
						Создавать новые подразделения
					</label>

					<div className={styles.buttons}>
						<Button onClick={handleUpload} disabled={!file || loading}>
							{renderUploadBtnText()}
						</Button>
					</div>
				</div>
			)}

			{phase === 'confirm' && draft && (
				<div className={styles.content}>
					<div className={styles.stats}>
						<div className={styles.statsRow}>
							<span>Будет создано сотрудников:</span>
							<strong>{draft.stats.users_to_create}</strong>
						</div>
						<div className={styles.statsRow}>
							<span>Новых профессий:</span>
							<strong>{draft.stats.professions_to_create}</strong>
						</div>
						<div className={styles.statsRow}>
							<span>Новых подразделений:</span>
							<strong>{draft.stats.divisions_to_create}</strong>
						</div>
						<div className={styles.statsRow}>
							<span>Пропущено:</span>
							<strong>{draft.stats.skipped}</strong>
						</div>
					</div>
					<p className={styles.hint}>Проверьте скачанный файл и подтвердите загрузку.</p>
					{confirmResult === 'partial' && confirmSummary && (
						<div className={styles.partialResult}>
							<p>Создано: <strong>{confirmSummary.created}</strong>, не удалось: <strong>{confirmSummary.failed}</strong></p>
							<ul className={styles.errorList}>
								{confirmSummary.errors.map((e, i) => (
									<li key={i}><strong>{e.name}</strong>: {e.reason}</li>
								))}
							</ul>
						</div>
					)}
					<div className={styles.buttons}>
						<Button
							onClick={handleConfirm}
							disabled={confirmLoading || confirmResult !== null}
							className={confirmResult === 'confirmed' ? styles.okBtn : ''}
						>
							{confirmResult === 'confirmed' ? '✓ Применено' : confirmResult === 'partial' ? '✓ Частично' : confirmLoading ? <Spinner /> : 'Применить'}
						</Button>
						<Button
							onClick={handleReject}
							disabled={confirmLoading || confirmResult !== null}
							className={confirmResult === 'rejected' ? styles.okBtn : ''}
						>
							{confirmResult === 'rejected' ? '✓ Отменено' : confirmLoading ? <Spinner /> : 'Отменить'}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}